// =============================================================================
// VulnTracker — API Hooks (React Query)
// =============================================================================
import { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/api/client';
import type {
  DashboardStats,
  Vulnerability,
  Component,
  VulnerabilityMatch,
  Notification,
  Project,
  Organization,
  PaginatedResponse,
  SeverityTrendPoint,
  EcosystemBreakdown,
  HealthStatus,
} from '@/types';

// ── Dashboard ────────────────────────────────────────────────────────────
// All dashboard queries use staleTime: 0 so navigating to the dashboard
// always triggers a fresh fetch, plus background polling every 30 s.
const DASHBOARD_QUERY_OPTS = {
  staleTime: 0,
  refetchInterval: 30_000,
  refetchOnWindowFocus: true,
  refetchOnMount: 'always' as const,
};

export function useDashboardStats() {
  return useQuery<DashboardStats>({
    queryKey: ['dashboard', 'stats'],
    queryFn: async () => (await api.get('/dashboard/stats')).data,
    ...DASHBOARD_QUERY_OPTS,
  });
}

export function useSeverityTrend(days = 30) {
  return useQuery<SeverityTrendPoint[]>({
    queryKey: ['dashboard', 'severity-trend', days],
    queryFn: async () => (await api.get(`/dashboard/severity-trend?days=${days}`)).data,
    ...DASHBOARD_QUERY_OPTS,
  });
}

export function useEcosystemBreakdown() {
  return useQuery<EcosystemBreakdown[]>({
    queryKey: ['dashboard', 'ecosystem-breakdown'],
    queryFn: async () => (await api.get('/dashboard/ecosystem-breakdown')).data,
    ...DASHBOARD_QUERY_OPTS,
  });
}

export function useRemediationStatus() {
  return useQuery<Array<{ status: string; count: number }>>({
    queryKey: ['dashboard', 'remediation-status'],
    queryFn: async () => (await api.get('/dashboard/remediation-status')).data,
    ...DASHBOARD_QUERY_OPTS,
  });
}

export function useTopAffectedProjects(limit = 10) {
  return useQuery({
    queryKey: ['dashboard', 'top-affected-projects', limit],
    queryFn: async () => (await api.get(`/dashboard/top-affected-projects?limit=${limit}`)).data,
    ...DASHBOARD_QUERY_OPTS,
  });
}

// ── Vulnerabilities ──────────────────────────────────────────────────────
export function useVulnerabilities(params: Record<string, string | number>) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      searchParams.set(key, String(value));
    }
  });

  return useQuery<PaginatedResponse<Vulnerability>>({
    queryKey: ['vulnerabilities', params],
    queryFn: async () => (await api.get(`/vulnerabilities?${searchParams}`)).data,
  });
}

export function useVulnerability(id: string) {
  return useQuery<Vulnerability>({
    queryKey: ['vulnerability', id],
    queryFn: async () => (await api.get(`/vulnerabilities/${id}`)).data,
    enabled: !!id,
  });
}

// ── Components ───────────────────────────────────────────────────────────
export function useComponents(params: Record<string, string | number>, options?: { enabled?: boolean }) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      searchParams.set(key, String(value));
    }
  });

  return useQuery<PaginatedResponse<Component>>({
    queryKey: ['components', params],
    queryFn: async () => (await api.get(`/components?${searchParams}`)).data,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateComponent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      project_id: string;
      name: string;
      version: string;
      ecosystem: string;
      purl?: string;
      cpe?: string;
      supplier?: string;
      group?: string;
      component_type?: string;
      description?: string;
      scope?: string;
      license_id?: string;
      is_direct?: boolean;
      hash_alg?: string;
      hash_value?: string;
    }) => (await api.post('/components', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['components'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateComponent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: {
      id: string;
      name?: string;
      version?: string;
      ecosystem?: string;
      component_type?: string;
      group?: string;
      supplier?: string;
      purl?: string;
      cpe?: string;
      license_id?: string;
      description?: string;
      scope?: string;
      is_direct?: boolean;
      hash_alg?: string;
      hash_value?: string;
    }) => (await api.patch(`/components/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['components'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteComponent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/components/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['components'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUploadSBOM() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      formatType,
      file,
    }: {
      projectId: string;
      formatType: string;
      file: File;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      return (
        await api.post(
          `/components/sbom/upload?project_id=${projectId}&format_type=${formatType}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
      ).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['components'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// ── Component Vulnerability Scanning ─────────────────────────────────────
export function useScanComponent() {
  const queryClient = useQueryClient();
  return useMutation<{
    component_id: string;
    component: string;
    source: 'database' | 'osv' | 'multi';
    vulnerabilities_found: number;
    sources_queried?: string[];
    source_counts?: Record<string, number>;
    errors?: string[] | null;
    items: ComponentVulnerability[];
  }, Error, string>({
    mutationFn: async (componentId: string) =>
      (await api.post(`/components/${componentId}/scan`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['components'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['vulnerabilities'] });
      queryClient.invalidateQueries({ queryKey: ['matches'] });
    },
  });
}

// Force-scan: always query external sources even if DB matches exist
export function useForceScanComponent() {
  const queryClient = useQueryClient();
  return useMutation<{
    component_id: string;
    component: string;
    source: 'database' | 'osv' | 'multi';
    vulnerabilities_found: number;
    sources_queried?: string[];
    source_counts?: Record<string, number>;
    errors?: string[] | null;
    items: ComponentVulnerability[];
  }, Error, string>({
    mutationFn: async (componentId: string) =>
      (await api.post(`/components/${componentId}/scan?force=true`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['components'] });
      queryClient.invalidateQueries({ queryKey: ['component-vulnerabilities'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['vulnerabilities'] });
      queryClient.invalidateQueries({ queryKey: ['matches'] });
    },
  });
}

// Bulk scan all components in a project — returns a task_id for polling
export function useScanAllComponents() {
  return useMutation<{
    task_id: string;
    total_components: number;
    message: string;
  }, Error, string>({
    mutationFn: async (projectId: string) =>
      (await api.post(`/components/project/${projectId}/scan-all`)).data,
  });
}

// Poll scan progress
export interface ScanProgress {
  status: 'running' | 'enriching' | 'completed';
  total: number;
  scanned: number;
  current_component: string;
  vulnerabilities_found: number;
  errors: string[];
  enrichment_total?: number;
  enrichment_done?: number;
}

const SCAN_POLL_MAX_MS = 90 * 60 * 1000; // 90 minutes max polling duration

export function useScanProgress(taskId: string | null) {
  // Track when polling started so we can enforce a hard ceiling
  const startedRef = useRef<number>(0);
  if (taskId && startedRef.current === 0) startedRef.current = Date.now();
  if (!taskId) startedRef.current = 0;

  return useQuery<ScanProgress>({
    queryKey: ['scan-progress', taskId],
    queryFn: async () => {
      try {
        const raw = (await api.get(`/components/scan-progress/${taskId}`)).data as ScanProgress & { status: string };
        // Treat 'running' and 'enriching' as in-progress; anything else is completed.
        const status: 'running' | 'enriching' | 'completed' =
          raw.status === 'running' || raw.status === 'enriching'
            ? (raw.status as 'running' | 'enriching')
            : 'completed';
        return { ...raw, status, errors: [] };
      } catch (err: unknown) {
        // 404 = task not found (server restarted and cleared in-memory progress).
        // Return a synthetic "completed" record so refetchInterval stops polling
        // immediately instead of looping forever until the 90-minute ceiling.
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          return {
            status: 'completed' as const,
            total: 0,
            scanned: 0,
            current_component: '',
            vulnerabilities_found: 0,
            errors: ['Scan task lost (server restarted). Please re-run the scan.'],
          };
        }
        throw err;
      }
    },
    enabled: !!taskId,
    retry: false, // Do not retry — 404 is handled above; other errors should surface immediately
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && data.status === 'completed') return false;
      if (startedRef.current && Date.now() - startedRef.current > SCAN_POLL_MAX_MS) return false;
      return 1000; // Poll every second while running
    },
  });
}

export interface ComponentVulnerability {
  match_id: string;
  vulnerability_id: string;
  cve_id: string | null;
  ghsa_id: string | null;
  osv_id: string | null;
  title: string;
  description: string;
  severity: string;
  cvss_v3_score: number | null;
  source: string | null;
  source_url: string | null;
  published_at: string | null;
  remediation_status: string;
  risk_score: number;
  matched_at: string | null;
  reachability: string;
  reachability_method: string;
  epss_score: number | null;
  epss_percentile: number | null;
  in_kev: boolean;
  cwe_ids: string[];
}

export function useComponentVulnerabilities(componentId: string | null) {
  return useQuery<{
    component_id: string;
    component: string;
    total: number;
    items: ComponentVulnerability[];
  }>({
    queryKey: ['component-vulnerabilities', componentId],
    queryFn: async () => (await api.get(`/components/${componentId}/vulnerabilities`)).data,
    enabled: !!componentId,
  });
}

// ── Matches ──────────────────────────────────────────────────────────────
export function useMatches(params: Record<string, string | number | boolean>) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      searchParams.set(key, String(value));
    }
  });

  return useQuery<PaginatedResponse<VulnerabilityMatch>>({
    queryKey: ['matches', params],
    queryFn: async () => (await api.get(`/matches?${searchParams}`)).data,
  });
}

export function useUpdateMatchStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      matchId,
      data,
    }: {
      matchId: string;
      data: { remediation_status?: string; notes?: string };
    }) => (await api.patch(`/matches/${matchId}/status`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['component-vulnerabilities'] });
    },
  });
}

export function useUpdateMatchVex() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      matchId,
      data,
    }: {
      matchId: string;
      data: { vex_status: string | null; vex_justification?: string };
    }) => (await api.patch(`/matches/${matchId}/vex`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      queryClient.invalidateQueries({ queryKey: ['component-vulnerabilities'] });
    },
  });
}

// ── Notifications ────────────────────────────────────────────────────────
export function useNotifications(params: Record<string, string | number>) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      searchParams.set(key, String(value));
    }
  });

  return useQuery<PaginatedResponse<Notification>>({
    queryKey: ['notifications', params],
    queryFn: async () => (await api.get(`/notifications?${searchParams}`)).data,
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string) =>
      (await api.post(`/notifications/${notificationId}/read`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post('/notifications/read-all')).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// ── Organizations & Projects ─────────────────────────────────────────────
export function useOrganizations(params: Record<string, string | number> = {}) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      searchParams.set(key, String(value));
    }
  });

  return useQuery<PaginatedResponse<Organization>>({
    queryKey: ['organizations', params],
    queryFn: async () => (await api.get(`/organizations?${searchParams}`)).data,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; description?: string }) =>
      (await api.post('/organizations', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, data }: { orgId: string; data: { name?: string; description?: string } }) =>
      (await api.patch(`/organizations/${orgId}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orgId: string) =>
      (await api.delete(`/organizations/${orgId}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useLinkProjectToOrg() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, organizationId }: { projectId: string; organizationId: string }) =>
      (await api.patch(`/projects/${projectId}`, { organization_id: organizationId })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useProjects(params: Record<string, string | number> = {}) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      searchParams.set(key, String(value));
    }
  });

  return useQuery<PaginatedResponse<Project>>({
    queryKey: ['projects', params],
    queryFn: async () => (await api.get(`/projects?${searchParams}`)).data,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      organization_id?: string;
      project_manager: string;
      security_expert: string;
      security_expert_email: string;
      description?: string;
    }) => (await api.post('/projects', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: {
      id: string;
      data: {
        name?: string;
        organization_id?: string;
        project_manager?: string;
        security_expert?: string;
        security_expert_email?: string;
        description?: string;
      };
    }) => (await api.patch(`/projects/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/projects/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['components'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// ── Health ────────────────────────────────────────────────────────────────
// Health endpoints are mounted at root (/) NOT under /api/v1
import axios from 'axios';
const _BASE = import.meta.env.VITE_API_URL ?? '';
const healthApi = axios.create({ baseURL: `${_BASE}/`, timeout: 10000 });

export function useHealthStatus() {
  return useQuery<HealthStatus>({
    queryKey: ['health'],
    queryFn: async () => (await healthApi.get('/health')).data,
    refetchInterval: 60000,
  });
}

export function useAppInfo() {
  return useQuery<{ app_name: string; version: string; environment: string; uptime_seconds: number }>({
    queryKey: ['appInfo'],
    queryFn: async () => (await healthApi.get('/info')).data,
    refetchInterval: 60000,
  });
}

export function useReadiness() {
  return useQuery<HealthStatus>({
    queryKey: ['readiness'],
    queryFn: async () => {
      try {
        return (await healthApi.get('/ready')).data;
      } catch (err: any) {
        // /ready returns 503 when degraded — body still has check details
        return err?.response?.data ?? { status: 'degraded', checks: {} };
      }
    },
    refetchInterval: 30000,
  });
}
// ── System Settings ───────────────────────────────────────────────────────
export function useSyncSchedule() {
  return useQuery<{ cve_sync_time: string }>({
    queryKey: ['settings', 'sync-schedule'],
    queryFn: async () => (await api.get('/settings/sync-schedule')).data,
    staleTime: 60_000,
  });
}

export function useUpdateSyncSchedule() {
  const queryClient = useQueryClient();
  return useMutation<{ cve_sync_time: string }, Error, string>({
    mutationFn: async (cve_sync_time: string) =>
      (await api.put('/settings/sync-schedule', { cve_sync_time })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'sync-schedule'] });
    },
  });
}